import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { LucideIcon } from "lucide-react";
import { FC, useRef } from "react";
import { useIntl } from "react-intl";
import { AdaptiveTabsTrigger } from "~/components/ui/adaptive-tabs";

interface SortableTabTriggerProps {
  id: string;
  value: string;
  label: string;
  icon: LucideIcon;
  isDragging?: boolean;
}

export const SortableTabTrigger: FC<SortableTabTriggerProps> = ({
  id,
  value,
  label,
  icon,
  isDragging = false,
}) => {
  const intl = useIntl();
  const isDraggingRef = useRef(false);

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    isDragging: isSortableDragging,
  } = useSortable({
    id,
    data: {
      type: "tab",
      tabId: id,
    },
  });

  const style = transform
    ? {
        transform: CSS.Transform.toString(transform),
        touchAction: "none",
      }
    : undefined;

  const handleMouseDown = (event: React.MouseEvent) => {
    isDraggingRef.current = false;
    if (listeners?.onMouseDown) {
      listeners.onMouseDown(event);
    }
  };

  const handleDragStart = () => {
    isDraggingRef.current = true;
  };

  const handleDragEnd = () => {
    setTimeout(() => {
      isDraggingRef.current = false;
    }, 0);
  };

  const handleClick = (event: React.MouseEvent) => {
    if (isDraggingRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };

  const handlePointerDown = (event: React.PointerEvent) => {
    isDraggingRef.current = false;
    if (listeners?.onPointerDown) {
      listeners.onPointerDown(event);
    }
  };

  const handlePointerUp = (event: React.PointerEvent) => {
    if (isDraggingRef.current) {
      event.preventDefault();
      event.stopPropagation();
      return false;
    }
  };

  return (
    <AdaptiveTabsTrigger
      ref={setNodeRef}
      style={style}
      value={value}
      label={intl.formatMessage({ id: label })}
      icon={icon}
      className={
        (isDragging || isSortableDragging ? "z-50 " : "") +
        "cursor-grab select-none active:cursor-grabbing"
      }
      {...attributes}
      {...listeners}
      onMouseDown={handleMouseDown}
      onPointerDown={handlePointerDown}
      onPointerUp={handlePointerUp}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onClick={handleClick}
      onKeyDown={(e) => {
        if (isDraggingRef.current && (e.key === "Enter" || e.key === " ")) {
          e.preventDefault();
          e.stopPropagation();
        }
      }}
    />
  );
};
